import { z } from "zod";

export type CoarseLocation = {
  city: string | null;
  country: string;
  countryCode: string;
};

const CITY_PATTERN = /^[\p{L}\p{M}0-9 .'-]{1,40}$/u;
const COUNTRY_PATTERN = /^[\p{L}\p{M} .'-]{1,56}$/u;

export const coarseLocationSchema = z.object({
  city: z.string().min(1).max(40).nullable(),
  country: z.string().min(1).max(56),
  countryCode: z.string().regex(/^[A-Z]{2}$/),
}) satisfies z.ZodType<CoarseLocation>;

const MOCK_LOCATIONS: readonly CoarseLocation[] = [
  { city: "Austin", country: "United States", countryCode: "US" },
  { city: "Lisbon", country: "Portugal", countryCode: "PT" },
  { city: "Toronto", country: "Canada", countryCode: "CA" },
  { city: "Berlin", country: "Germany", countryCode: "DE" },
  { city: "Seoul", country: "South Korea", countryCode: "KR" },
  { city: "Mexico City", country: "Mexico", countryCode: "MX" },
  { city: "Melbourne", country: "Australia", countryCode: "AU" },
  { city: "Nairobi", country: "Kenya", countryCode: "KE" },
  { city: "Osaka", country: "Japan", countryCode: "JP" },
  { city: "São Paulo", country: "Brazil", countryCode: "BR" },
  { city: "Stockholm", country: "Sweden", countryCode: "SE" },
  { city: "Mumbai", country: "India", countryCode: "IN" },
];

function hashSeed(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

function tidyPlaceName(
  value: string | null | undefined,
  max: number,
): string | null {
  if (!value) return null;
  const trimmed = value.normalize("NFC").replace(/\s+/g, " ").trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

export function sanitizeCoarseLocation(input: {
  city?: string | null;
  country?: string | null;
  countryCode?: string | null;
}): CoarseLocation | null {
  const countryCode = (input.countryCode ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(countryCode)) return null;
  const country =
    tidyPlaceName(input.country, 56) ?? countryNameFromCode(countryCode);
  if (!country || !COUNTRY_PATTERN.test(country)) return null;
  const city = tidyPlaceName(input.city, 40);
  if (city && !CITY_PATTERN.test(city)) {
    return { city: null, country, countryCode };
  }
  return { city, country, countryCode };
}

export function countryNameFromCode(countryCode: string): string | null {
  try {
    return (
      new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode) ?? null
    );
  } catch {
    return null;
  }
}

export function countryFlagEmoji(countryCode: string): string {
  const code = countryCode.toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  return String.fromCodePoint(
    127_397 + code.charCodeAt(0),
    127_397 + code.charCodeAt(1),
  );
}

export function formatCoarseLocation(location: CoarseLocation): string {
  return location.city
    ? `${location.city}, ${location.country}`
    : location.country;
}

export function formatCoarseLocationShort(location: CoarseLocation): string {
  return location.city
    ? `${location.city}, ${location.countryCode}`
    : location.countryCode;
}

export function mockLocationFor(seed: string): CoarseLocation {
  return (
    MOCK_LOCATIONS[hashSeed(seed) % MOCK_LOCATIONS.length] ?? {
      city: "Austin",
      country: "United States",
      countryCode: "US",
    }
  );
}
